import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Text } from '@/nova/components';

import { isSafeVoucher } from '../../../../lib/startProperties';
import { useConfigurator } from '../../../../store/configurator';
import { ApplyButton, Input, InputRow, Reveal, RevealLink, Root, Status, Title } from './VoucherField.styles';

/** Which inline message an apply outcome maps to. Kept as a small union so each message can be
 *  rendered as a LITERAL `<Text>` below — the i18n extractor only harvests literal keys. */
type Feedback = 'used' | 'network' | 'invalid';

function toFeedback(error: string | null): Feedback {
  if (error === 'VOUCHER_ALREADY_REDEEMED' || error === 'VOUCHER_REDEMPTION_LIMIT_REACHED') return 'used';
  if (error === 'NETWORK' || error === 'UNAVAILABLE') return 'network';
  return 'invalid';
}

export type VoucherFieldProps = {
  /** Horizontal padding (px) so the heading + input line up with the billing rows above, which are
   *  indented by their container's padding: 16 on desktop (card + row), 8 in the mobile summary. */
  inset?: number;
};

/**
 * "Enter a voucher code" control for the checkout review: shown ONLY while no voucher is applied.
 * Collapsed by default to a quiet "Have a voucher code?" link; clicking it reveals (and focuses) the
 * input + Apply, and an emptied, blurred input with no message folds back to the link. Once a code is
 * applied it hides; the applied code then lives in the billing summary's "Discount Voucher" row as a
 * removable chip ({@link VoucherChip}). Applying persists the code to the session; the backend
 * re-evaluates it and the discount line + total update reactively.
 * Self-wired via {@link useConfigurator}; pass `inset` to align it with the rows above.
 */
export const VoucherField = ({ inset = 0 }: VoucherFieldProps): React.ReactElement | null => {
  const { t } = useTranslation();
  const { billing, applyVoucher, voucherPending } = useConfigurator();

  const appliedCode = billing?.voucherCode ?? null;
  const [draft, setDraft] = useState('');
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // DOM sync: focus the input the moment the link reveals it.
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const trimmed = draft.trim();
  const canApply = trimmed.length > 0 && !voucherPending;

  const onApply = async (): Promise<void> => {
    if (!canApply) return;
    // Reject an obviously-malformed code before a round-trip; the backend is still the authority.
    if (!isSafeVoucher(trimmed)) {
      setFeedback('invalid');
      return;
    }
    const result = await applyVoucher(trimmed);
    if (result.applied) {
      setDraft('');
      setFeedback(null);
      setOpen(false);
    } else {
      setFeedback(toFeedback(result.error));
    }
  };

  // A voucher is on the order → the removable chip in the billing row IS the whole control; no input.
  if (appliedCode) return null;

  if (!open) {
    return (
      <Root $inset={inset}>
        <RevealLink type="button" onClick={() => setOpen(true)}>
          <Text text="Have a voucher code?" tag="span" />
        </RevealLink>
      </Root>
    );
  }

  return (
    <Root $inset={inset}>
      <Title text="Have a voucher code?" tag="span" />
      <Reveal>
        <InputRow>
          <Input
            ref={inputRef}
            type="text"
            value={draft}
            aria-label={t('Voucher code')}
            placeholder={t('Enter voucher code')}
            disabled={voucherPending}
            maxLength={64}
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            onChange={(event) => {
              setDraft(event.target.value);
              if (feedback) setFeedback(null);
            }}
            onBlur={() => {
              // Nothing typed and nothing to report: fold back to the link.
              if (!draft.trim() && !feedback && !voucherPending) setOpen(false);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void onApply();
              }
            }}
          />
          <ApplyButton type="button" onClick={() => void onApply()} disabled={!canApply}>
            <Text text="Apply" tag="span" />
          </ApplyButton>
        </InputRow>
        {feedback === 'used' ? (
          <Status $tone="error" text="This voucher has already been used." tag="span" />
        ) : feedback === 'network' ? (
          <Status $tone="error" text="Couldn't apply the voucher. Please try again." tag="span" />
        ) : feedback === 'invalid' ? (
          <Status $tone="error" text="This voucher code isn't valid." tag="span" />
        ) : null}
      </Reveal>
    </Root>
  );
};

export default VoucherField;
